import { useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import ImagemProduto from '@/components/feature/ImagemProduto';
import { formatarCentavos } from '@/pages/loja/utils';
import { NAVY, OURO } from '@/pages/loja/identidade';

export interface LinhaProdutoProps {
  nome: string;
  descricao?: string | null;
  precoCentavos: number;
  imagemUrl: string | null;
  imagemSlug: string | null;
  emoji: string | null;
  quantidade: number;
  /** Maior quantidade que a pessoa pode escolher. */
  maximo: number;
  /** Fora de venda: mostra o rótulo no lugar do seletor. */
  travado?: boolean;
  travadoRotulo?: string;
  /** "Esgotou", "Últimas", "só inscritos"… */
  etiqueta?: string | null;
  aoMudarQuantidade: (valor: number) => void;
}

// ---------------------------------------------------------------------------
// A LINHA da loja (padrão único da vitrine de fichas e da loja pública):
// foto 64 px (ou o ícone da barraca, nunca um bloco vazio), nome, descrição em
// uma linha, preço dourado e o seletor − [n] + com botões de 48 px.
// Fundo escuro #10203A, cartão com borda clara, cantos de 16 px.
// ---------------------------------------------------------------------------
export default function LinhaProduto({
  nome,
  descricao,
  precoCentavos,
  imagemUrl,
  imagemSlug,
  emoji,
  quantidade,
  maximo,
  travado = false,
  travadoRotulo = '—',
  etiqueta = null,
  aoMudarQuantidade,
}: LinhaProdutoProps) {
  const reduzir = useReducedMotion();
  const [pulso, setPulso] = useState(0);

  const temFoto = Boolean(imagemUrl || imagemSlug);
  const noMaximo = quantidade >= maximo;

  function aumentar() {
    if (travado || noMaximo) return;
    aoMudarQuantidade(quantidade + 1);
    setPulso((k) => k + 1);
  }

  function diminuir() {
    if (travado) return;
    aoMudarQuantidade(Math.max(0, quantidade - 1));
  }

  return (
    <div
      className={`flex items-center gap-3 rounded-[16px] border px-3 py-3 transition-colors duration-200 ${
        quantidade > 0
          ? 'border-white/35 bg-white/[0.08]'
          : 'border-white/[0.09] bg-white/[0.03]'
      } ${travado ? 'opacity-55' : ''}`}
    >
      {temFoto ? (
        <ImagemProduto
          imagemUrl={imagemUrl}
          imagemSlug={imagemSlug}
          emoji={emoji}
          nome={nome}
          tamanho="media"
          lazy
          className="!h-16 !w-16 !rounded-[12px]"
        />
      ) : (
        <span
          aria-hidden
          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[12px] border border-white/10 bg-white/[0.07]"
          style={{ color: OURO }}
        >
          <i className="ri-store-2-line text-[24px]" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-[16px] font-semibold leading-tight text-white">{nome}</p>

        {descricao && (
          <p className="mt-0.5 truncate text-[13px] leading-snug text-white/55">{descricao}</p>
        )}

        <p className="mt-1 flex items-baseline gap-2">
          <span className="text-[16px] font-bold" style={{ color: OURO }}>
            {formatarCentavos(precoCentavos)}
          </span>
          {etiqueta && (
            <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-white/55">
              {etiqueta}
            </span>
          )}
        </p>
      </div>

      {travado ? (
        <span className="flex h-12 w-[124px] shrink-0 items-center justify-center rounded-[12px] border border-white/10 text-[11px] font-semibold text-white/45">
          {travadoRotulo}
        </span>
      ) : (
        <div className="flex shrink-0 items-center">
          <button
            type="button"
            onClick={diminuir}
            disabled={quantidade === 0}
            aria-label={`Diminuir ${nome}`}
            className="aperta flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-white/15 text-[19px] leading-none text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-25"
          >
            −
          </button>
          <motion.span
            key={pulso}
            animate={pulso > 0 && !reduzir ? { scale: [1, 1.15, 1] } : { scale: 1 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="w-7 text-center text-[20px] font-extrabold"
            style={{ color: quantidade > 0 ? OURO : 'rgba(255,255,255,.7)' }}
          >
            {quantidade}
          </motion.span>
          <button
            type="button"
            onClick={aumentar}
            disabled={noMaximo}
            aria-label={`Aumentar ${nome}`}
            className={`aperta flex h-12 w-12 cursor-pointer items-center justify-center rounded-full text-[20px] leading-none transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${
              quantidade > 0 ? '' : 'border border-white/15 text-white hover:bg-white/10'
            }`}
            style={quantidade > 0 ? { background: OURO, color: NAVY } : undefined}
          >
            +
          </button>
        </div>
      )}
    </div>
  );
}